import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import api from './api/client';

// ── Auth Context ──────────────────────────────────────────────────────────────
const AuthContext = createContext(null);

export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    api.me().then(data => {
      setUser(data);
    }).catch(() => {
      localStorage.removeItem('token');
    }).finally(() => setLoading(false));
  }, []);

  const login = (tokenData) => {
    localStorage.setItem('token', tokenData.access_token);
    setUser(tokenData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) return <div className="loading-page"><div className="spinner"/></div>;

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export function isSuperAdmin(user) { return (user?.id === 1) || (user?.user_id === 1); }
export function isModerator(user) { return user?.is_admin && !isSuperAdmin(user); }

// ── Pages ─────────────────────────────────────────────────────────────────────
const LoginPage      = React.lazy(() => import('./pages/LoginPage'));
const RegisterPage      = React.lazy(() => import('./pages/RegisterPage'));
const RegisterModPage   = React.lazy(() => import('./pages/RegisterModPage'));
const LoginModPage      = React.lazy(() => import('./pages/LoginModPage'));
const ResetPage      = React.lazy(() => import('./pages/ResetPage'));
const HomePage       = React.lazy(() => import('./pages/HomePage'));
const SubmitPage     = React.lazy(() => import('./pages/SubmitPage'));
const StatisticsPage = React.lazy(() => import('./pages/StatisticsPage'));
const AbsencePage    = React.lazy(() => import('./pages/AbsencePage'));
const ProfilePage    = React.lazy(() => import('./pages/ProfilePage'));
const AdminPage      = React.lazy(() => import('./pages/AdminPage'));

// Модераторские страницы
const ModHomePage       = React.lazy(() => import('./pages/mod/ModHomePage'));
const ModSubmitPage     = React.lazy(() => import('./pages/mod/ModSubmitPage'));
const ModStatisticsPage = React.lazy(() => import('./pages/mod/ModStatisticsPage'));
const ModAbsencePage    = React.lazy(() => import('./pages/mod/ModAbsencePage'));
const ModProfilePage    = React.lazy(() => import('./pages/mod/ModProfilePage'));

// ── Bottom Nav для жильцов ────────────────────────────────────────────────────
function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const path = location.pathname;

  const items = [
    { to: '/',           icon: '🏠', label: 'Главная' },
    { to: '/submit',     icon: '📝', label: 'Показания' },
    { to: '/statistics', icon: '📈', label: 'Статистика' },
    { to: '/absence',    icon: '🏖️', label: 'Отсутствие' },
    { to: '/profile',    icon: '👤', label: 'Профиль' },
  ];
  if (isSuperAdmin(user)) items.push({ to: '/admin', icon: '⚙️', label: 'Админ' });

  return (
    <nav className="bottom-nav">
      {items.map(item => (
        <button key={item.to}
          className={`nav-item ${path === item.to ? 'active' : ''}`}
          onClick={() => nav(item.to)}>
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ── Bottom Nav для модераторов ─────────────────────────────────────────────────
function ModBottomNav() {
  const location = useLocation();
  const nav = useNavigate();
  const path = location.pathname;

  const items = [
    { to: '/mod',            icon: '🏠', label: 'Главная' },
    { to: '/mod/submit',     icon: '📊', label: 'Показания' },
    { to: '/mod/statistics', icon: '📈', label: 'Статистика' },
    { to: '/mod/absence',    icon: '🏖️', label: 'Отсутствие' },
    { to: '/mod/profile',    icon: '⚙️', label: 'Управление' },
  ];

  return (
    <nav className="bottom-nav">
      {items.map(item => (
        <button key={item.to}
          className={`nav-item ${path === item.to ? 'active' : ''}`}
          onClick={() => nav(item.to)}>
          <span className="nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ── Protected Routes ──────────────────────────────────────────────────────────
function Protected({ children, adminOnly = false, superAdminOnly = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (superAdminOnly && !isSuperAdmin(user)) return <Navigate to="/" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function ModProtected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isModerator(user)) return <Navigate to="/" replace />;
  return children;
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app">
          <React.Suspense fallback={<div className="loading-page"><div className="spinner"/></div>}>
            <AppRoutes />
          </React.Suspense>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const publicPaths = ['/login', '/register', '/register-mod', '/login-mod', '/reset-password'];
  const isPublic = publicPaths.some(p => location.pathname.startsWith(p));
  const isModPath = location.pathname.startsWith('/mod');

  // Модератор попадает на /mod, супер-админ и жильцы на /
  const homeRedirect = () => {
    if (!user) return '/login';
    if (isModerator(user)) return '/mod';
    return '/';
  };

  return (
    <>
      <Routes>
        {/* Публичные */}
        <Route path="/login"          element={user ? <Navigate to={homeRedirect()} /> : <LoginPage />} />
        <Route path="/register"       element={user ? <Navigate to={homeRedirect()} /> : <RegisterPage />} />
        <Route path="/reset-password" element={<ResetPage />} />
        <Route path="/register-mod" element={user ? <Navigate to={homeRedirect()} /> : <RegisterModPage />} />
        <Route path="/login-mod" element={user ? <Navigate to={homeRedirect()} /> : <LoginModPage />} />

        {/* Жильцы и супер-админ */}
        <Route path="/"           element={<Protected><HomePage /></Protected>} />
        <Route path="/submit"     element={<Protected><SubmitPage /></Protected>} />
        <Route path="/statistics" element={<Protected><StatisticsPage /></Protected>} />
        <Route path="/absence"    element={<Protected><AbsencePage /></Protected>} />
        <Route path="/profile"    element={<Protected><ProfilePage /></Protected>} />
        <Route path="/admin/*"    element={<Protected superAdminOnly><AdminPage /></Protected>} />

        {/* Модераторы */}
        <Route path="/mod"            element={<ModProtected><ModHomePage /></ModProtected>} />
        <Route path="/mod/submit"     element={<ModProtected><ModSubmitPage /></ModProtected>} />
        <Route path="/mod/statistics" element={<ModProtected><ModStatisticsPage /></ModProtected>} />
        <Route path="/mod/absence"    element={<ModProtected><ModAbsencePage /></ModProtected>} />
        <Route path="/mod/profile"    element={<ModProtected><ModProfilePage /></ModProtected>} />

        <Route path="*" element={<Navigate to={user ? homeRedirect() : '/login'} />} />
      </Routes>

      {/* Навигация */}
      {user && !isPublic && !isModPath && <BottomNav />}
      {user && isModPath && <ModBottomNav />}
    </>
  );
}
